import { useMemo, useState } from 'react';
import { PreferenceInput } from 'sr-types/lib/account/v1/graphql';
import { AllPermissions } from '../auth/api';
import { deletePreferenceMutation, savePreferenceMutation } from '../auth/Preferences';
import type { Entity } from '../constants/customSortFieldConfigs';
import { FORMAT_API_ACTION } from '../constants/reportConstants';
import { failedToastMessage, successToastMessage } from '../utils/commonUtils';
import { makeReportTemplatesPreferenceKey } from './reportPreferenceUtils';
import type { TemplatePreference } from './reportTypes';
import { buildTemplateConfigPayload, extractReportConfigurations } from './reportUtils';

export function useReportTemplatePreference(preferences, state) {
    const { matchingPreference, isViewOnly } = useMemo(() => {
        if (!preferences?.length) {
            return { matchingPreference: null, isViewOnly: false };
        }

        const pref = state?.selectedId ? (preferences.find((p) => p?.id === state?.selectedId) ?? null) : null;

        const allowed = pref?.security?.allowedActions ?? [];
        const viewOnly = Boolean(pref) && !allowed.includes(AllPermissions.Edit);

        return { matchingPreference: pref, isViewOnly: viewOnly };
    }, [preferences, state?.selectedId]);

    {
        return { matchingPreference, isViewOnly };
    }
}

export function useSaveExistingReportTemplate({
    matchingPreference,
    state,
    isViewOnly,
    userAccountId,
    application,
    setInitialContent,
    handleMenuClose,
    refreshFn,
    setIsEditing,
    entity,
    preferenceEntityKey
}) {
    const [loading, setLoading] = useState(false);

    async function saveExistingReportTemplate(configureStepData) {
        setLoading(true);
        try {
            if (!matchingPreference?.id) {
                console.warn('No matching preference found — use Save As instead.');
                return;
            }

            if (isViewOnly) {
                console.warn('No write permission for this template.');
                return;
            }

            if (!preferenceEntityKey.trim()) {
                failedToastMessage('report.template.save.failed');
                return;
            }

            const currentRich = state?.rich ?? '{}';
            const reportConfigurations = extractReportConfigurations(configureStepData, entity);
            const templateConfig = buildTemplateConfigPayload(reportConfigurations, entity);

            const jsonToSave = {
                templateConfig,
                template: currentRich ?? {}
            };

            const prefPayload: PreferenceInput = {
                identity: { id: matchingPreference.id },
                key: makeReportTemplatesPreferenceKey(preferenceEntityKey),
                value: JSON.stringify(jsonToSave, null, 0),
                application: application.name,
                accountId: matchingPreference?.accountId,
                name: matchingPreference?.name,
                summary: matchingPreference?.summary
            };

            const res = await savePreferenceMutation(userAccountId, prefPayload);
            if (!res?.data?.savePreference?.errors) {
                setInitialContent(jsonToSave.template);
                handleMenuClose?.();
                setIsEditing(false);
                successToastMessage('report.template.save.success');

                if (typeof refreshFn === 'function') {
                    const timeoutId = setTimeout(refreshFn, 2000);
                    return () => clearTimeout(timeoutId);
                }
            }
            return;
        } catch (err) {
            console.error('Save error', err);
            failedToastMessage('report.template.save.failed');
        } finally {
            setLoading(false);
        }
    }

    return { saveExistingReportTemplate, saveExistingReportLoading: loading };
}
export function useSaveNewReportTemplatePreference({
    userProfile,
    activeOrganizationAccount,
    organizationAccountDetails,
    application,
    entity,
    preferenceEntityKey
}: {
    userProfile: any;
    activeOrganizationAccount: string;
    organizationAccountDetails: any;
    application: { name: string };
    entity: Entity;
    preferenceEntityKey: string;
}) {
    const [loading, setLoading] = useState(false);

    async function saveReportTemplatePreference(
        values,
        template,
        setSaveAsModalOpen,
        configureStepData,
        shared = true
    ) {
        setLoading(true);
        try {
            if (!preferenceEntityKey.trim()) {
                failedToastMessage('report.template.save.failed');
                return null;
            }

            const accountId = userProfile?.accountId;

            const resolvedConfiguration = extractReportConfigurations(configureStepData, entity);
            const preferenceValue = {
                templateConfig: buildTemplateConfigPayload(resolvedConfiguration, entity),
                template: template ?? {}
            };

            const preferencePayload: PreferenceInput = {
                key: makeReportTemplatesPreferenceKey(preferenceEntityKey),
                value: JSON.stringify(preferenceValue, null, 0),
                application: application.name,
                name: values.templateName?.trim() || 'Untitled Report Template',
                summary: {
                    about: values.templateDescription?.trim() || '',
                    headline: ''
                }
            };

            const shareInput = shared
                ? {
                      sharedWithOrganizationAccounts: [
                          {
                              actor: {
                                  id: activeOrganizationAccount,
                                  name: organizationAccountDetails.organizationName || ''
                              },
                              action: FORMAT_API_ACTION.manage
                          }
                      ]
                  }
                : null;

            const res = await savePreferenceMutation(accountId, preferencePayload, shareInput);

            if (res?.data?.savePreference?.id) {
                const serializedPreferenceValue = JSON.stringify(preferenceValue, null, 0);

                const newTemplate = {
                    id: res?.data?.savePreference?.id,
                    name: preferencePayload.name,
                    description: preferencePayload.summary.about,
                    type: 'Report',
                    template,
                    __raw: {
                        accountId: userProfile?.accountId,
                        application: application.name,
                        agent: '',
                        identity: { id: res?.data?.savePreference?.id },
                        key: makeReportTemplatesPreferenceKey(preferenceEntityKey),
                        name: preferencePayload.name,
                        security: { allowedActions: [] },

                        summary: preferencePayload.summary,
                        value: serializedPreferenceValue,
                        view: ''
                    }
                };

                successToastMessage('report.template.save.success');
                setSaveAsModalOpen(false);
                return newTemplate;
            }
        } catch (err) {
            console.warn('Error saving report template:', err);
            failedToastMessage('report.template.save.failed');
            return null;
        } finally {
            setLoading(false);
        }
    }

    return { saveReportTemplatePreference, saveReportLoading: loading };
}
export async function optimisticDeletePreference({
    id,
    localTemplates,
    setLocalTemplates,
    deleteFn = deletePreferenceMutation,
    refreshFn,
    accountId,
    ownerId
}: {
    id: string;
    localTemplates: TemplatePreference[];
    setLocalTemplates: (templates: TemplatePreference[]) => void;
    deleteFn?: (accountId: string, id: string) => Promise<any>;
    refreshFn?: (ownerId?: string) => Promise<void>;
    accountId: string;
    ownerId: string;
}) {
    const prev = localTemplates;
    const updated = prev.filter((t) => t.id !== id);
    setLocalTemplates(updated);

    const rollback = () => setLocalTemplates(prev);

    try {
        const res = await deleteFn(accountId, id);
        const hasError = !!res?.data?.deletePreference?.errors;
        if (hasError) {
            rollback();
            failedToastMessage('report.template.delete.failure');
            return false;
        }

        successToastMessage('report.template.delete.success');

        if (typeof refreshFn === 'function') {
            const timeoutId = setTimeout(refreshFn, 2000);
            return () => clearTimeout(timeoutId);
        }

        return true;
    } catch (err) {
        rollback();
        failedToastMessage('report.template.delete.failure');
        return false;
    }
}
